import type { UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import type {
  BatteryProposalDto,
  BatteryProposalPricingDto,
  UpdateBatteryProposalDetailsDto,
} from "../dto/battery-proposal.dto"
import { batteryProposalService } from "../services/battery-proposal.service"

const isConfigurationDetailsSave = (
  body: UpdateBatteryProposalDetailsDto,
): boolean => `batterySystemType` in body

const applySuccessfulPricingCache = (
  queryClient: ReturnType<typeof useQueryClient>,
  proposalId: string,
  pricing: BatteryProposalPricingDto,
): void => {
  queryClient.setQueryData(
    queryKeys.batteryProposal.pricing(proposalId),
    pricing,
  )
  void queryClient.invalidateQueries({
    queryKey: queryKeys.batteryProposal.byId(proposalId),
    exact: true,
  })
  void queryClient.invalidateQueries({
    queryKey: queryKeys.batteryProposal.incentives(proposalId),
  })
  void queryClient.invalidateQueries({
    queryKey: queryKeys.batteryProposal.financialSummary(proposalId),
  })
  void queryClient.invalidateQueries({
    queryKey: queryKeys.batteryProposal.contract(proposalId),
  })
  void queryClient.invalidateQueries({
    queryKey: queryKeys.batteryProposal.financing(proposalId),
  })
}

const markPricingOutOfDate = (
  queryClient: ReturnType<typeof useQueryClient>,
  proposalId: string,
): void => {
  void queryClient.invalidateQueries({
    queryKey: queryKeys.batteryProposal.pricing(proposalId),
  })
  void queryClient.invalidateQueries({
    queryKey: queryKeys.batteryProposal.financialSummary(proposalId),
  })
  void queryClient.invalidateQueries({
    queryKey: queryKeys.batteryProposal.contract(proposalId),
  })
  void queryClient.invalidateQueries({
    queryKey: queryKeys.batteryProposal.financing(proposalId),
  })
}

export default function useUpdateBatteryProposalDetails(): UseMutationResult<
  BatteryProposalDto,
  unknown,
  { proposalId: string; body: UpdateBatteryProposalDetailsDto }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ proposalId, body }) => {
      const proposal = await batteryProposalService.updateBatteryProposalDetails(
        proposalId,
        body,
      )

      // Requested Fixed Margin-only saves refresh pricing inside the mutation
      // so the chain survives drawer unmount (RQ4 drops mutate() callbacks).
      if (!isConfigurationDetailsSave(body)) {
        try {
          const pricing = await batteryProposalService.postPricing(proposalId)
          applySuccessfulPricingCache(queryClient, proposalId, pricing)
        } catch {
          // Margin save already persisted; surface out_of_date instead of
          // leaving a cached `current` status for up to staleTime.
          markPricingOutOfDate(queryClient, proposalId)
        }
      }

      return proposal
    },
    onSuccess: (proposal, { proposalId, body }) => {
      queryClient.setQueryData(
        queryKeys.batteryProposal.byId(proposalId),
        proposal,
      )
      void queryClient.invalidateQueries({
        queryKey: queryKeys.batteryProposal.byId(proposalId),
      })
      // Configuration saves leave retained pricing out of date — refetch.
      // Margin-only saves already refreshed or marked out of date above.
      if (isConfigurationDetailsSave(body)) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.batteryProposal.pricing(proposalId),
        })
        void queryClient.invalidateQueries({
          queryKey: queryKeys.batteryProposal.inputs(proposalId),
        })
      }
      void queryClient.invalidateQueries({
        queryKey: queryKeys.batteryProposal.financialSummary(proposalId),
      })
      // Flavor, stored total, and pricing freshness all feed contract readiness.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.batteryProposal.contract(proposalId),
      })
      // A Finance Partner change can clear the saved Loan Product.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.batteryProposal.financing(proposalId),
      })
    },
  })
}
